import { Component } from '@theme/component';

/**
 * Displays a two-digit value for each countdown unit.
 */
class CountdownTimerFlip extends HTMLElement {
  #initialized = false;
  #isAnimating = false;
  #pendingAnimations = 0;
  #currentValue = '00';
  /** @type {Array<{ digit: HTMLSpanElement, current: HTMLSpanElement, next: HTMLSpanElement }>} */
  #digitElements = [];

  connectedCallback() {
    this.#initialize();
  }

  /**
   * @param {number | string} value
   */
  set value(value) {
    this.#initialize();
    if (!this.#digitElements.length) return;

    const formattedValue = String(Math.max(0, Number(value) || 0));

    if (formattedValue.length !== this.#digitElements.length) {
      this.#setValueWithDigitReset(formattedValue);
      return;
    }

    if (formattedValue === this.#currentValue) return;

    const animationStyle = this.getAttribute('data-animation-style') || 'default';
    const animationDirection = this.getAttribute('data-animation-direction') || 'up';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (animationStyle === 'default' || reducedMotion) {
      this.#setValueImmediately(formattedValue);
      return;
    }

    this.#animateValue(formattedValue, animationStyle, animationDirection);
  }

  #initialize() {
    if (this.#initialized) return;

    this.#initialized = true;
    this.#currentValue = (this.textContent || '00').trim();

    this.#renderDigits(this.#currentValue);
    this.addEventListener('animationend', this.#onAnimationEnd);
  }

  /** @param {string} value */
  #setValueWithDigitReset(value) {
    this.#isAnimating = false;
    this.#pendingAnimations = 0;
    this.#currentValue = value;
    this.classList.remove('is-animating', 'animate', 'is-flip', 'is-rotate', 'is-direction-up', 'is-direction-down');
    this.#renderDigits(value);
  }

  /** @param {string} value */
  #renderDigits(value) {
    this.textContent = '';
    this.#digitElements = [];

    for (const [index, digitValue] of Array.from(value).entries()) {
      const digit = document.createElement('span');
      digit.className = 'countdown-timer-flip__digit';
      digit.dataset.index = String(index);

      const current = document.createElement('span');
      current.className = 'countdown-timer-flip__value countdown-timer-flip__value--current';
      current.textContent = digitValue;

      const next = document.createElement('span');
      next.className = 'countdown-timer-flip__value countdown-timer-flip__value--next';
      next.textContent = digitValue;

      digit.append(current, next);
      this.append(digit);
      this.#digitElements.push({ digit, current, next });
    }
  }

  /**
   * @param {{ current: HTMLSpanElement, next: HTMLSpanElement, digit: HTMLSpanElement }} elements
   * @param {string} value
   */
  #settleDigitElements(elements, value) {
    elements.current.textContent = value;
    elements.next.textContent = value;
    elements.digit.classList.remove('is-changing');
    elements.current.removeAttribute('style');
    elements.next.removeAttribute('style');
    elements.next.setAttribute('aria-hidden', 'true');
  }

  /** @param {string} value */
  #setValueImmediately(value) {
    if (!this.#digitElements.length) return;

    this.#isAnimating = false;
    this.#pendingAnimations = 0;
    this.#currentValue = value;

    for (const [index, elements] of this.#digitElements.entries()) {
      const nextValue = value[index] || '0';
      this.#settleDigitElements(elements, nextValue);
    }

    this.classList.remove('is-animating', 'animate', 'is-flip', 'is-rotate', 'is-direction-up', 'is-direction-down');
  }

  /**
   * @param {string} value
   * @param {string} animationStyle
   * @param {string} animationDirection
   */
  #animateValue(value, animationStyle, animationDirection) {
    if (!this.#digitElements.length) return;

    if (this.#isAnimating) {
      this.#finishAnimation();
    }

    let changedDigits = 0;

    for (const [index, elements] of this.#digitElements.entries()) {
      const currentValue = this.#currentValue[index] || '0';
      const nextValue = value[index] || '0';
      const changed = currentValue !== nextValue;

      elements.digit.classList.toggle('is-changing', changed);
      elements.next.textContent = nextValue;
      elements.next.toggleAttribute('aria-hidden', !changed);

      if (changed) changedDigits++;
    }

    if (changedDigits === 0) {
      this.#setValueImmediately(value);
      return;
    }

    this.#isAnimating = true;
    this.#pendingAnimations = changedDigits;

    this.classList.toggle('is-flip', animationStyle === 'flip');
    this.classList.toggle('is-rotate', animationStyle === 'rotate');
    this.classList.toggle('is-direction-up', animationDirection === 'up');
    this.classList.toggle('is-direction-down', animationDirection === 'down');
    this.classList.add('is-animating');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('animate');
      });
    });
  }

  /** @param {AnimationEvent} event */
  #onAnimationEnd = (event) => {
    if (!this.#isAnimating) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.classList.contains('countdown-timer-flip__value--next')) return;

    const digitElement = target.closest('.countdown-timer-flip__digit');
    if (!(digitElement instanceof Element)) return;
    if (!digitElement.classList.contains('is-changing')) return;

    this.#pendingAnimations = Math.max(0, this.#pendingAnimations - 1);

    if (this.#pendingAnimations === 0) {
      this.#finishAnimation();
    }
  };

  #finishAnimation() {
    if (!this.#digitElements.length) return;

    this.#isAnimating = false;
    this.#pendingAnimations = 0;

    let nextValue = '';

    for (const elements of this.#digitElements) {
      const value = elements.next.textContent || '0';
      this.#settleDigitElements(elements, value);
      nextValue += value;
    }

    this.#currentValue = nextValue;
    this.classList.remove('animate', 'is-animating', 'is-flip', 'is-rotate', 'is-direction-up', 'is-direction-down');
  }
}

/**
 * @typedef {object} Refs
 * @property {CountdownTimerFlip[]} [digits]
 */

/**
 * Countdown timer element used in the condensed header section.
 *
 * @extends {Component<Refs>}
 */
class CountdownTimer extends Component {
  static get observedAttributes() {
    return [
      'expires-at',
      'data-expiration-date',
      'data-expiration-timestamp',
      'starts-at',
      'data-scheduled-start',
      'data-start-timestamp',
      'data-store-timezone',
      'data-store-timezone-offset',
      'data-store-timezone-is-override',
      'expiration-behavior',
      'data-days-label',
      'data-hours-label',
      'data-minutes-label',
      'data-seconds-label',
      'data-animation-style',
      'data-animation-direction',
    ];
  }

  /** @type {number | undefined} */
  #interval = undefined;
  /** @type {number | null} */
  #expirationTimestamp = null;
  /** @type {number | null} */
  #startTimestamp = null;
  #labels = {
    days: 'Days',
    hours: 'Hours',
    minutes: 'Min',
    seconds: 'Sec',
  };
  #lastLiveMinuteKey = '';
  /** @type {boolean} */
  #wasVisible = false;

  /** @typedef {'days' | 'hours' | 'minutes' | 'seconds'} CountdownUnit */

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === newValue || !this.isConnected) return;
    this.refresh();
  }

  connectedCallback() {
    super.connectedCallback();
    this.refresh();
  }

  disconnectedCallback() {
    this.#stopInterval();
  }

  refresh() {
    this.#loadLabels();
    this.#loadAnimationStyle();

    const isExpired = this.#render(true);
    const startTimestamp = this.#resolveStartTimestamp();
    const beforeStart = startTimestamp && Date.now() < startTimestamp;
    const gated = this.#isVisibilityGated();

    this.#syncVisibility({ animate: gated });

    if (!isExpired || beforeStart) {
      this.#startInterval();
    }
  }

  #loadLabels() {
    const daysLabel = this.getAttribute('data-days-label');
    const hoursLabel = this.getAttribute('data-hours-label');
    const minutesLabel = this.getAttribute('data-minutes-label');
    const secondsLabel = this.getAttribute('data-seconds-label');

    if (daysLabel) this.#labels.days = daysLabel;
    if (hoursLabel) this.#labels.hours = hoursLabel;
    if (minutesLabel) this.#labels.minutes = minutesLabel;
    if (secondsLabel) this.#labels.seconds = secondsLabel;

    this.#updateUnitLabels();
  }

  #loadAnimationStyle() {
    const animationStyle = this.getAttribute('data-animation-style') || 'default';
    const animationDirection = this.getAttribute('data-animation-direction') || 'up';
    const digits = this.querySelectorAll('countdown-timer-flip[type]');

    for (const digit of digits) {
      digit.setAttribute('data-animation-style', animationStyle);
      digit.setAttribute('data-animation-direction', animationDirection);
    }
  }

  #updateUnitLabels() {
    const unitElements = this.querySelectorAll('.countdown-bar__timer-unit');
    /** @type {CountdownUnit[]} */
    const types = ['days', 'hours', 'minutes', 'seconds'];
    let typeIndex = 0;

    unitElements.forEach((unit) => {
      const type = types[typeIndex];
      if (!type) return;

      unit.textContent = this.#labels[type];
      typeIndex++;
    });
  }

  /** @param {boolean} [forceLiveAnnouncement=false] */
  #render(forceLiveAnnouncement = false) {
    const startTimestamp = this.#resolveStartTimestamp();
    
    if (startTimestamp && Date.now() < startTimestamp) {
      this.#setValues({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      this.#updateLiveRegion({ days: 0, hours: 0, minutes: 0, seconds: 0 }, false);
      return true;
    }

    const expirationTimestamp = this.#resolveExpirationTimestamp();

    if (expirationTimestamp === null) {
      this.#setValues({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      this.#updateLiveRegion({ days: 0, hours: 0, minutes: 0, seconds: 0 }, true);
      return true;
    }

    const remaining = Math.max(0, expirationTimestamp - Date.now());

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#setValues({ days, hours, minutes, seconds });

    const liveMinuteKey = `${days}:${hours}:${minutes}`;
    const shouldAnnounceLiveRegion = forceLiveAnnouncement || remaining === 0 || this.#lastLiveMinuteKey !== liveMinuteKey;
    if (shouldAnnounceLiveRegion) {
      this.#lastLiveMinuteKey = liveMinuteKey;
      this.#updateLiveRegion({ days, hours, minutes, seconds }, remaining === 0);
    }

    if (remaining === 0) {
      this.#handleExpiration();
      return true;
    }

    return false;
  }

  #startInterval() {
    if (this.#interval) return;

    this.#interval = setInterval(() => {
      this.#render();
      this.#syncVisibility({ animate: this.#isVisibilityGated() });
    }, 1000);
  }

  #stopInterval() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /** @returns {HTMLElement | null} */
  #getVisibilityRoot() {
    const root = this.closest('[data-countdown-visibility-root]');
    if (root instanceof HTMLElement) return root;

    const bar = this.closest('.countdown-bar');
    return bar instanceof HTMLElement ? bar : null;
  }

  #isVisibilityGated() {
    const root = this.#getVisibilityRoot();
    return root?.hasAttribute('data-visibility-gated') ?? false;
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #shouldBeVisible() {
    if (window.Shopify?.designMode) return true;

    const startTimestamp = this.#resolveStartTimestamp();
    if (startTimestamp && Date.now() < startTimestamp) return false;

    const expirationTimestamp = this.#resolveExpirationTimestamp();
    if (expirationTimestamp !== null) {
      const remaining = expirationTimestamp - Date.now();
      if (remaining <= 0 && this.getAttribute('expiration-behavior') === 'hide') return false;
    }

    return true;
  }

  /** @param {HTMLElement} root */
  #clearVisibilityAnimationClasses(root) {
    root.classList.remove('countdown-bar-root--entering', 'countdown-bar-root--exiting', 'countdown-bar-root--visible');
  }

  /**
   * @param {{ animate?: boolean }} [options]
   */
  #syncVisibility(options = {}) {
    const { animate = false } = options;
    const root = this.#getVisibilityRoot();
    if (!root) return;

    if (root.classList.contains('countdown-bar-root--exiting') || root.classList.contains('countdown-bar-root--entering')) {
      return;
    }

    const shouldShow = this.#shouldBeVisible();
    const wasVisible = this.#wasVisible;
    const isDomVisible = !root.hasAttribute('hidden');

    if (shouldShow) {
      if (!isDomVisible) {
        root.removeAttribute('hidden');

        const shouldAnimate = animate && wasVisible === false && !this.#prefersReducedMotion();
        if (shouldAnimate) {
          this.#clearVisibilityAnimationClasses(root);
          root.classList.add('countdown-bar-root--entering');

          /** @param {AnimationEvent} event */
          const onEnterEnd = (event) => {
            if (event.target !== root || event.animationName !== 'elementSlideInTop') return;
            root.removeEventListener('animationend', onEnterEnd);
            root.classList.remove('countdown-bar-root--entering');
            root.classList.add('countdown-bar-root--visible');
          };

          root.addEventListener('animationend', onEnterEnd);
        } else {
          this.#clearVisibilityAnimationClasses(root);
          root.classList.add('countdown-bar-root--visible');
        }
      }

      this.#wasVisible = true;
      return;
    }

    if (!isDomVisible) {
      this.#wasVisible = false;
      return;
    }

    const shouldAnimate = animate && wasVisible === true && !this.#prefersReducedMotion();
    if (shouldAnimate) {
      this.#clearVisibilityAnimationClasses(root);
      root.classList.add('countdown-bar-root--exiting');

      /** @param {AnimationEvent} event */
      const onExitEnd = (event) => {
        if (event.target !== root || event.animationName !== 'elementSlideOutTop') return;
        root.removeEventListener('animationend', onExitEnd);
        root.setAttribute('hidden', 'hidden');
        this.#clearVisibilityAnimationClasses(root);
        this.#wasVisible = false;
      };

      root.addEventListener('animationend', onExitEnd);
      return;
    }

    root.setAttribute('hidden', 'hidden');
    this.#clearVisibilityAnimationClasses(root);
    this.#wasVisible = false;
  }

  /** @param {string} attributeName */
  #resolveTimestampAttribute(attributeName) {
    const value = this.getAttribute(attributeName);
    if (!value) return null;

    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;

    // Liquid's %s outputs epoch seconds.
    if (parsed < 1_000_000_000_000) {
      return parsed * 1000;
    }

    return parsed;
  }

  /**
   * Resolves the store IANA timezone for parsing merchant-entered datetimes.
   * `shop.timezone` is not exposed in Liquid storefronts, so we infer from offset when needed.
   *
   * @returns {string | null}
   */
  #resolveStoreTimeZone() {
    const timeZone = this.getAttribute('data-store-timezone')?.trim();

    if (timeZone && timeZone.includes('/')) {
      try {
        new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
        return timeZone;
      } catch {
        // Fall through to offset inference.
      }
    }

    const offset = this.getAttribute('data-store-timezone-offset')?.trim();
    return this.#inferIANAFromOffset(offset);
  }

  /**
   * Maps a Shopify `date: "%z"` offset to a representative IANA zone.
   * Ambiguous offsets exist (e.g. -0700), but this matches the store primary timezone
   * for typical North American Shopify stores better than a hard-coded fallback.
   *
   * @param {string | null | undefined} offset
   * @returns {string | null}
   */
  #inferIANAFromOffset(offset) {
    if (!offset) return null;

    const normalized = offset.replace(':', '');
    /** @type {Record<string, string>} */
    const offsetToIANA = {
      '-1200': 'Pacific/Honolulu',
      '-1100': 'Pacific/Pago_Pago',
      '-1000': 'America/Anchorage',
      '-0900': 'America/Anchorage',
      '-0800': 'America/Los_Angeles',
      '-0700': 'America/Los_Angeles',
      '-0600': 'America/Chicago',
      '-0500': 'America/New_York',
      '-0400': 'America/Halifax',
      '+0000': 'Etc/UTC',
      '+0100': 'Europe/London',
      '+0200': 'Europe/Berlin',
      '+0800': 'Asia/Singapore',
      '+0900': 'Asia/Tokyo',
      '+1000': 'Australia/Sydney',
    };

    const inferred = offsetToIANA[normalized];
    if (!inferred) return null;

    try {
      new Intl.DateTimeFormat('en-US', { timeZone: inferred }).format(new Date());
      return inferred;
    } catch {
      return null;
    }
  }

  /** @param {string} rawDate */
  #parseRawDateParts(rawDate) {
    const match = rawDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) return null;

    return {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
      hour: Number(match[4]),
      minute: Number(match[5]),
      second: Number(match[6] || '0'),
    };
  }

  /**
   * @param {number} timestamp
   * @param {string} timeZone
   */
  #getTimeZoneOffsetMilliseconds(timestamp, timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });

      const parts = formatter.formatToParts(new Date(timestamp));
      const values = {
        year: Number.NaN,
        month: Number.NaN,
        day: Number.NaN,
        hour: Number.NaN,
        minute: Number.NaN,
        second: Number.NaN,
      };

      for (const part of parts) {
        if (part.type in values) {
          const key = /** @type {keyof typeof values} */ (part.type);
          values[key] = Number(part.value);
        }
      }

      if (
        Number.isNaN(values.year) ||
        Number.isNaN(values.month) ||
        Number.isNaN(values.day) ||
        Number.isNaN(values.hour) ||
        Number.isNaN(values.minute) ||
        Number.isNaN(values.second)
      ) {
        return null;
      }

      const asUtc = Date.UTC(values.year, values.month - 1, values.day, values.hour, values.minute, values.second);
      return asUtc - timestamp;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} rawDate
   * @param {string} timeZone
   */
  #resolveZonedRawTimestamp(rawDate, timeZone) {
    const parts = this.#parseRawDateParts(rawDate);
    if (!parts) return null;

    const utcGuess = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    const firstOffset = this.#getTimeZoneOffsetMilliseconds(utcGuess, timeZone);
    if (firstOffset === null) return null;

    let timestamp = utcGuess - firstOffset;
    const secondOffset = this.#getTimeZoneOffsetMilliseconds(timestamp, timeZone);

    if (secondOffset !== null && secondOffset !== firstOffset) {
      timestamp = utcGuess - secondOffset;
    }

    return timestamp;
  }

  #hasStoreTimeZoneOverride() {
    return this.hasAttribute('data-store-timezone-is-override');
  }

  #resolveExpirationTimestamp() {
    const rawExpirationDate = this.getAttribute('data-expiration-date');
    const storeTimeZone = this.#resolveStoreTimeZone();

    if (this.#hasStoreTimeZoneOverride() && rawExpirationDate && storeTimeZone) {
      const parsedOverride = this.#resolveZonedRawTimestamp(rawExpirationDate, storeTimeZone);
      if (parsedOverride !== null) {
        this.#expirationTimestamp = parsedOverride;
        return parsedOverride;
      }
    }

    const expiresAt = this.getAttribute('expires-at');

    if (expiresAt) {
      const parsedDate = Date.parse(expiresAt);
      if (!Number.isNaN(parsedDate)) {
        this.#expirationTimestamp = parsedDate;
        return parsedDate;
      }
    }

    if (rawExpirationDate && storeTimeZone) {
      const parsedRawDate = this.#resolveZonedRawTimestamp(rawExpirationDate, storeTimeZone);
      if (parsedRawDate !== null) {
        this.#expirationTimestamp = parsedRawDate;
        return parsedRawDate;
      }
    }

    const timestampFromAttribute = this.#resolveTimestampAttribute('data-expiration-timestamp');

    if (timestampFromAttribute !== null) {
      this.#expirationTimestamp = timestampFromAttribute;
      return timestampFromAttribute;
    }

    if (rawExpirationDate) {
      const normalizedRawDate = rawExpirationDate.trim().replace(' ', 'T');
      const parsedRawDate = Date.parse(normalizedRawDate);
      if (!Number.isNaN(parsedRawDate)) {
        this.#expirationTimestamp = parsedRawDate;
        return parsedRawDate;
      }
    }

    this.#expirationTimestamp = null;
    return null;
  }

  #resolveStartTimestamp() {
    const rawStart = this.getAttribute('data-scheduled-start');
    const storeTimeZone = this.#resolveStoreTimeZone();

    if (this.#hasStoreTimeZoneOverride() && rawStart && storeTimeZone) {
      const parsedOverride = this.#resolveZonedRawTimestamp(rawStart, storeTimeZone);
      if (parsedOverride !== null) {
        this.#startTimestamp = parsedOverride;
        return parsedOverride;
      }
    }

    const startsAt = this.getAttribute('starts-at');

    if (startsAt) {
      const parsed = Date.parse(startsAt);
      if (!Number.isNaN(parsed)) {
        this.#startTimestamp = parsed;
        return parsed;
      }
    }

    if (rawStart && storeTimeZone) {
      const parsedRawStart = this.#resolveZonedRawTimestamp(rawStart, storeTimeZone);
      if (parsedRawStart !== null) {
        this.#startTimestamp = parsedRawStart;
        return parsedRawStart;
      }
    }

    const timestampFromAttribute = this.#resolveTimestampAttribute('data-start-timestamp');

    if (timestampFromAttribute !== null) {
      this.#startTimestamp = timestampFromAttribute;
      return timestampFromAttribute;
    }

    if (rawStart) {
      const normalized = rawStart.trim().replace(' ', 'T');
      const parsed = Date.parse(normalized);
      if (!Number.isNaN(parsed)) {
        this.#startTimestamp = parsed;
        return parsed;
      }
    }

    this.#startTimestamp = null;
    return null;
  }

  /**
   * @param {{days: number, hours: number, minutes: number, seconds: number}} values
   * @param {boolean} isExpired
   */
  #updateLiveRegion(values, isExpired) {
    const liveElement = this.querySelector('[data-countdown-live]');
    if (!(liveElement instanceof HTMLElement)) return;

    if (isExpired) {
      liveElement.textContent = `0 ${this.#labels.days}, 0 ${this.#labels.hours}, 0 ${this.#labels.minutes}, 0 ${this.#labels.seconds}`;
      return;
    }

    liveElement.textContent = `${values.days} ${this.#labels.days}, ${values.hours} ${this.#labels.hours}, ${values.minutes} ${this.#labels.minutes}, ${values.seconds} ${this.#labels.seconds}`;
  }

  /**
   * @param {{days: number, hours: number, minutes: number, seconds: number}} values
   */
  #setValues(values) {
    const digits = this.querySelectorAll('countdown-timer-flip[type]');

    for (const digitElement of digits) {
      const digit = /** @type {CountdownTimerFlip} */ (digitElement);
      const type = digit.getAttribute('type');
      if (!type) continue;

      if (!(type in values)) continue;

      const typedKey = /** @type {keyof typeof values} */ (type);
      const rawValue = values[typedKey];
      digit.value = type === 'days' ? rawValue : String(rawValue).padStart(2, '0');
    }
  }

  #handleExpiration() {
    this.#syncVisibility({ animate: this.#isVisibilityGated() });
    this.stop();
  }

  stop() {
    this.#stopInterval();
  }
}

if (!customElements.get('countdown-timer-flip')) {
  customElements.define('countdown-timer-flip', CountdownTimerFlip);
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}

const countdownEditorHooksFlag = 'data-countdown-timer-editor-hooks';

if (window.Shopify?.designMode && !document.documentElement.hasAttribute(countdownEditorHooksFlag)) {
  document.documentElement.setAttribute(countdownEditorHooksFlag, 'true');

  /**
   * @param {Event} event
  * @param {(timer: CountdownTimer) => void} callback
   */
  const forTimersInSection = (event, callback) => {
    const target = event.target;

    if (!(target instanceof Element)) {
      const allTimers = document.querySelectorAll('countdown-timer');
      for (const timer of allTimers) {
        if (timer instanceof CountdownTimer) {
          callback(timer);
        }
      }
      return;
    }

    const scope = target.classList.contains('shopify-section') ? target : target.closest('.shopify-section') || target;
    const timers = scope.querySelectorAll('countdown-timer');

    for (const timer of timers) {
      if (timer instanceof CountdownTimer) {
        callback(timer);
      }
    }
  };

  document.addEventListener('shopify:section:load', (event) => {
    forTimersInSection(event, /** @param {CountdownTimer} timer */ (timer) => {
      timer.refresh();
    });
  });

  document.addEventListener('shopify:section:unload', (event) => {
    forTimersInSection(event, /** @param {CountdownTimer} timer */ (timer) => {
      timer.stop();
    });
  });
}
